import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { generateSlug, getCoverUrl, isScheduled, formatSchedule, formatPostDate, type Post } from "@/lib/blog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MediaPicker } from "./MediaLibrary";
import { PREVIEW_KEY } from "@/pages/BlogPreview";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ArrowLeft,
  Code,
  LayoutGrid,
  Monitor,
  Smartphone,
  Sparkles,
  Wand2,
  Library,
  CalendarClock,
  X,
  Loader2,
  Search,
  ArrowUpDown,
  ExternalLink,
} from "lucide-react";
import { Columns, Column, Box } from "./editor/Blocks";
import PageBuilder, { parseDoc, serializeDoc, emptyDoc, LAYOUT_SUGGESTIONS, type Doc } from "./editor/PageBuilder";
import TemplateMenu, { loadTemplates } from "./editor/TemplateMenu";

import { loadCategories } from "./CategoriesManager";
import { useConfirm } from "./ConfirmDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

type StatusFilter = "all" | "published" | "scheduled" | "draft";
type SortBy = "recent" | "oldest" | "title-asc" | "title-desc" | "category";

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "published", label: "Publicados" },
  { key: "scheduled", label: "Agendados" },
  { key: "draft", label: "Rascunhos" },
];

const dateOf = (p: Post) => new Date(p.published_at ?? p.created_at).getTime();

const countWords = (html: string) =>
  html.replace(/<!--[\s\S]*?-->/g, "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").split(/\s+/).filter(Boolean).length;

const BlogManager = () => {
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Post | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("recent");
  useEffect(() => {
    if (params.get("new") !== "1") return;
    setCreating(true);
    const next = new URLSearchParams(params);
    next.delete("new");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("posts" as never)
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Erro ao carregar posts", description: error.message, variant: "destructive" });
    }
    setPosts((data as unknown as Post[]) ?? []);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const toggleStatus = async (post: Post) => {
    const next = post.status === "published" ? "draft" : "published";
    const { error } = await supabase
      .from("posts" as never)
      .update({
        status: next,
        published_at: next === "published" ? post.published_at ?? new Date().toISOString() : post.published_at,
      } as never)
      .eq("id", post.id);
    if (error) {
      toast({ title: "Erro ao alterar status", description: error.message, variant: "destructive" });
    } else {
      toast({ title: next === "published" ? "Post publicado" : "Post despublicado" });
      loadPosts();
    }
  };

  const copyLink = async (post: Post) => {
    await navigator.clipboard.writeText(`${window.location.origin}/blog/${post.slug}`);
    toast({ title: "Link copiado" });
  };

  const publishNow = async (post: Post) => {
    const { error } = await supabase.from("posts" as never).update({ status: "published", published_at: new Date().toISOString() } as never).eq("id", post.id);
    if (error) toast({ title: "Erro ao publicar", description: error.message, variant: "destructive" });
    else { toast({ title: "Artigo publicado agora" }); loadPosts(); }
  };

  const deletePost = async (post: Post) => {
    const ok = await confirm({ title: "Excluir artigo?", description: `"${post.title}" será excluído permanentemente. Essa ação não pode ser desfeita.`, confirmLabel: "Excluir", destructive: true });
    if (!ok) return;
    const { error } = await supabase.from("posts" as never).delete().eq("id", post.id);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Post excluído" });
      loadPosts();
    }
  };

  if (creating || editing) {
    return (
      <PostEditor
        post={editing}
        onClose={(saved) => {
          setCreating(false);
          setEditing(null);
          if (saved) loadPosts();
        }}
      />
    );
  }

  const q = search.toLowerCase().trim();
  const filtered = posts
    .filter((p) => {
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        (p.category ?? "").toLowerCase().includes(q) ||
        (p.excerpt ?? "").toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "scheduled" ? isScheduled(p) : p.status === statusFilter);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return dateOf(a) - dateOf(b);
        case "title-asc":
          return a.title.localeCompare(b.title, "pt-BR");
        case "title-desc":
          return b.title.localeCompare(a.title, "pt-BR");
        case "category":
          return (
            (a.category ?? "zzz").localeCompare(b.category ?? "zzz", "pt-BR") ||
            a.title.localeCompare(b.title, "pt-BR")
          );
        default:
          return dateOf(b) - dateOf(a);
      }
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative w-full max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, categoria ou resumo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
            <SelectTrigger className="w-[190px] gap-2">
              <ArrowUpDown size={14} className="text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Mais recentes</SelectItem>
              <SelectItem value="oldest">Mais antigos</SelectItem>
              <SelectItem value="title-asc">Título (A–Z)</SelectItem>
              <SelectItem value="title-desc">Título (Z–A)</SelectItem>
              <SelectItem value="category">Categoria</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setCreating(true)} className="gap-2">
            <Plus size={16} /> Novo artigo
          </Button>
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              statusFilter === tab.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-muted-foreground">Carregando...</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-muted-foreground">
          {posts.length === 0
            ? 'Nenhum artigo ainda. Clique em "Novo artigo" para começar.'
            : "Nenhum artigo encontrado com esses filtros."}
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((post) => (
          <Card key={post.id} className="overflow-hidden transition-colors hover:border-primary/40">
            <CardContent className="p-4 flex items-center gap-4 flex-wrap sm:flex-nowrap">
              <CoverThumb path={post.cover_image} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{post.title}</p>
                  {isScheduled(post) ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 flex items-center gap-1">
                      <CalendarClock size={12} /> Agendado para {formatSchedule(post.published_at!)}
                    </span>
                  ) : (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        post.status === "published"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {post.status === "published" ? "Publicado" : "Rascunho"}
                    </span>
                  )}
                  {post.category && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {post.category}
                    </span>
                  )}
                </div>
                {post.excerpt && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{post.excerpt}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1.5">
                  {formatPostDate(post.published_at ?? post.created_at)} · /blog/{post.slug}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0 items-center">
                {post.status === "published" && !isScheduled(post) && (
                  <>
                    <Button variant="ghost" size="icon" asChild title="Ver no site" aria-label="Ver no site">
                      <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /></a>
                    </Button>
                    <Button variant="ghost" size="icon" title="Copiar link" aria-label="Copiar link" onClick={() => copyLink(post)}>
                      <LinkIcon size={15} />
                    </Button>
                  </>
                )}
                {isScheduled(post) ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1"><CalendarClock size={14} /> Agendado</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => publishNow(post)}>Publicar agora (antecipar)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditing(post)}>Alterar data</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(post)}>Voltar para rascunho</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => toggleStatus(post)} className="gap-1">
                    {post.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                    {post.status === "published" ? "Despublicar" : "Publicar"}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setEditing(post)} className="gap-1">
                  <Pencil size={14} /> Editar
                </Button>
                <Button variant="ghost" size="icon" onClick={() => deletePost(post)} title="Excluir" aria-label="Excluir">
                  <Trash2 size={15} className="text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {dialog}
    </div>
  );
};

const CoverThumb = ({ path }: { path: string | null }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (!path) {
      setUrl(null);
      return;
    }
    getCoverUrl(path).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [path]);
  return (
    <div className="w-28 h-20 shrink-0 rounded-lg overflow-hidden bg-muted border border-border/60 flex items-center justify-center">
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <ImageIcon size={20} className="text-muted-foreground/60" />
      )}
    </div>
  );
};

const PostEditor = ({ post, onClose }: { post: Post | null; onClose: (saved: boolean) => void }) => {
  const { toast } = useToast();
  const [title, setTitle] = useState(post?.title ?? "");
  const [slug, setSlug] = useState(post?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(!!post);
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? "");
  const [category, setCategory] = useState(post?.category ?? "");
  const [categories, setCategories] = useState<string[]>(post?.category ? [post.category] : []);
  useEffect(() => {
    loadCategories().then((cs) => {
      const names = cs.map((c) => c.name);
      if (post?.category && !names.includes(post.category)) names.push(post.category);
      setCategories(names);
      if (!post && names[0]) setCategory((c) => c || names[0]);
    });
  }, [post]);
  const [coverPath, setCoverPath] = useState(post?.cover_image ?? "");
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [metaTitle, setMetaTitle] = useState(post?.meta_title ?? "");
  const [metaDescription, setMetaDescription] = useState(post?.meta_description ?? "");
  const [keywords, setKeywords] = useState((post?.keywords ?? []).join(", "));
  const toLocal = (iso: string) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
  const [scheduleAt, setScheduleAt] = useState(post && isScheduled(post) ? toLocal(post.published_at!) : "");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [libTarget, setLibTarget] = useState<"inline" | "cover" | null>(null);
  const [seoLoading, setSeoLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [review, setReview] = useState<Review | null>(null);

  const initialDoc = parseDoc(post?.content ?? "");
  const [mode, setMode] = useState<"builder" | "visual" | "code">(initialDoc ? "builder" : "visual");
  const [code, setCode] = useState("");
  const [doc, setDoc] = useState<Doc>(initialDoc ?? emptyDoc());

  // New article: use the account's default layout, if one is set
  useEffect(() => {
    if (post) return;
    loadTemplates().then((ts) => {
      const def = ts.find((t) => t.is_default);
      if (def) { setDoc(def.doc); setMode("builder"); }
    });
  }, [post]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Columns,
      Column,
      Box,
    ],
    content: initialDoc ? "" : post?.content ?? "",
    editorProps: {
      attributes: {
        class:
          "prose prose-neutral max-w-none min-h-[300px] p-4 focus:outline-none",
      },
    },
  });

  const currentHTML = () =>
    mode === "builder" ? serializeDoc(doc).replace(/^<!--abb-builder:.*?-->/, "") : mode === "code" ? code : editor?.getHTML() ?? "";

  const hasText = () => (mode === "code" ? code.trim() : editor?.getText().trim() ?? "").length > 0;

  const switchMode = (next: "builder" | "visual" | "code") => {
    if (next === mode) return;
    if (next === "builder") {
      if (hasText() && !window.confirm("O construtor começa uma estrutura nova. O conteúdo atual do modo texto/código não será convertido. Continuar?")) return;
    } else if (mode === "builder" && !window.confirm("Sair do construtor converte o artigo em texto e as seções deixam de ser editáveis como blocos. Continuar?")) return;
    const html = currentHTML();
    if (next === "code") setCode(html);
    if (next === "visual") editor?.commands.setContent(html);
    setMode(next);
  };

  const applyTemplate = (d: Doc) => {
    if (mode !== "builder" && hasText() && !window.confirm("Aplicar o layout substitui o conteúdo atual. Continuar?")) return;
    setDoc(structuredClone(d));
    setMode("builder");
  };

  const getContent = () => {
    if (mode === "builder") return serializeDoc(doc);
    if (mode === "code") return code;
    return editor?.getHTML() ?? "";
  };

  useEffect(() => {
    if (coverPath) getCoverUrl(coverPath).then(setCoverPreview);
  }, [coverPath]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) setSlug(generateSlug(value));
  };

  // Warn before leaving with unsaved changes
  const dirtyRef = useRef(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [coverDrag, setCoverDrag] = useState(false);
  useEffect(() => {
    if (!editor) return;
    const mark = () => { dirtyRef.current = true; };
    editor.on("update", mark);
    return () => { editor.off("update", mark); };
  }, [editor]);
  useEffect(() => { dirtyRef.current = true; }, [title, excerpt, coverPath, metaTitle, metaDescription, keywords, code, doc]);
  useEffect(() => { const t = setTimeout(() => { dirtyRef.current = false; }, 800); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const uploadCover = async (file: File) => {
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("blog-covers").upload(path, file);
    if (error) {
      toast({ title: "Erro no upload da imagem", description: error.message, variant: "destructive" });
      return;
    }
    setCoverPath(path);
  };

  const addLink = () => {
    const url = window.prompt("URL do link:");
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  };

  const imageInputRef = useRef<HTMLInputElement>(null);
  const addImage = () => imageInputRef.current?.click();
  const uploadInlineImage = async (file: File) => {
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `inline/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("blog-covers").upload(path, file);
    if (error) {
      toast({ title: "Erro no upload da imagem", description: error.message, variant: "destructive" });
      return;
    }
    const { data } = await supabase.storage.from("blog-covers").createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (data?.signedUrl) editor?.chain().focus().setImage({ src: data.signedUrl }).run();
  };

  const openPreview = async () => {
    localStorage.setItem(PREVIEW_KEY, JSON.stringify({
      title, category, coverUrl: coverPreview,
      publishedAt: scheduleAt ? new Date(scheduleAt).toISOString() : post?.published_at ?? null,
      content: getContent(),
    }));
    setPreviewOpen(true);
  };

  const callAI = async (mode: "seo" | "review") => {
    const { data, error } = await supabase.functions.invoke("blog-ai", {
      body: { mode, title, excerpt, content: getContent() },
    });
    if (error) {
      let msg = error.message;
      try { const b = await (error as { context?: Response }).context?.json(); if (b?.error) msg = typeof b.error === "string" ? b.error : "Dados inválidos"; } catch { /* ignore */ }
      toast({ title: "IA indisponível", description: msg, variant: "destructive" });
      return null;
    }
    return data?.result;
  };

  const generateSEO = async () => {
    setSeoLoading(true);
    const r = await callAI("seo");
    setSeoLoading(false);
    if (!r) return;
    setMetaTitle(r.meta_title);
    setMetaDescription(r.meta_description);
    setKeywords([...r.primary_keywords, ...r.secondary_keywords].join(", "));
    if (!post || post.status !== "published") { setSlug(generateSlug(r.slug)); setSlugTouched(true); }
    if (!excerpt.trim()) setExcerpt(r.meta_description);
    toast({ title: "SEO gerado", description: "Revise os campos antes de salvar." });
  };

  const runReview = async () => {
    setReviewLoading(true);
    const r = await callAI("review");
    setReviewLoading(false);
    if (r) setReview(r);
  };

  const pickMedia = (url: string, path: string) => {
    if (libTarget === "cover") setCoverPath(path);
    else editor?.chain().focus().setImage({ src: url }).run();
  };

  const save = async (action: "draft" | "now" | "schedule") => {
    if (!title.trim()) {
      toast({ title: "Dê um título ao artigo", variant: "destructive" });
      return;
    }
    const finalSlug = slug.trim() || generateSlug(title);
    setSaving(true);
    const content = getContent();
    const wasLive = post?.status === "published" && !isScheduled(post);
    const payload = {
      title: title.trim(),
      slug: finalSlug,
      excerpt: excerpt.trim() || null,
      content,
      category: category || null,
      cover_image: coverPath || null,
      meta_title: metaTitle.trim() || null,
      meta_description: metaDescription.trim() || null,
      keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
      status: action === "draft" ? "draft" : "published",
      published_at:
        action === "schedule"
          ? new Date(scheduleAt).toISOString()
          : action === "now"
            ? wasLive && post?.published_at ? post.published_at : new Date().toISOString()
            : post?.published_at ?? null,
      author_id: post?.author_id ?? (await supabase.auth.getUser()).data.user?.id ?? null,
    };

    const query = post
      ? supabase.from("posts" as never).update(payload as never).eq("id", post.id)
      : supabase.from("posts" as never).insert(payload as never);

    const { error } = await query;
    setSaving(false);
    if (error) {
      toast({
        title: "Erro ao salvar",
        description: error.message.includes("duplicate") ? "Já existe um artigo com esse endereço. Ajuste o slug." : error.message,
        variant: "destructive",
      });
    } else {
      dirtyRef.current = false;
      toast({
        title: action === "schedule" ? `Agendado para ${formatSchedule(payload.published_at!)}` : action === "now" ? (wasLive ? "Artigo atualizado!" : "Artigo publicado!") : "Rascunho salvo",
      });
      onClose(true);
    }
  };

  const toolbarBtn = (
    onClick: () => void,
    icon: React.ReactNode,
    label: string,
    active = false
  ) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`p-2 rounded hover:bg-muted transition-colors ${active ? "bg-muted text-primary" : ""}`}
    >
      {icon}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => (dirtyRef.current ? setLeaveOpen(true) : onClose(false))} className="gap-2">
          <ArrowLeft size={16} /> Voltar
        </Button>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openPreview} className="gap-1.5">
            <Eye size={15} /> Prévia
          </Button>
          <Button variant="outline" onClick={runReview} disabled={reviewLoading} className="gap-1.5">
            {reviewLoading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Revisar
          </Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving}>
            Salvar rascunho
          </Button>
          <div className="flex">
            <Button onClick={() => save("now")} disabled={saving} className="rounded-r-none">
              {saving ? "Salvando..." : post?.status === "published" && !isScheduled(post) ? "Atualizar" : "Publicar agora"}
            </Button>
            <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <PopoverTrigger asChild>
                <Button disabled={saving} className="rounded-l-none border-l border-primary-foreground/20 px-2.5 gap-1" title="Agendar publicação">
                  <CalendarClock size={15} />
                  {scheduleAt && <span className="text-xs">{formatSchedule(new Date(scheduleAt).toISOString())}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 space-y-3">
                <p className="text-sm font-medium flex items-center gap-1.5"><CalendarClock size={14} className="text-primary" /> Agendar publicação</p>
                <Input type="datetime-local" value={scheduleAt} min={toLocal(new Date().toISOString())} onChange={(e) => setScheduleAt(e.target.value)} />
                <div className="flex gap-2">
                  {scheduleAt && <Button variant="ghost" size="sm" onClick={() => setScheduleAt("")}>Limpar</Button>}
                  <Button size="sm" className="flex-1" disabled={!scheduleAt || new Date(scheduleAt) <= new Date()} onClick={() => { setScheduleOpen(false); save("schedule"); }}>
                    Confirmar agendamento
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Input
            placeholder="Título do artigo"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="text-lg font-semibold"
          />
          <div className="flex gap-1 p-1 rounded-lg bg-muted w-fit">
            {(["visual", "code", "builder"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 ${mode === m ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                {m === "builder" ? <LayoutGrid size={14} /> : m === "visual" ? <Pencil size={14} /> : <Code size={14} />}
                {m === "builder" ? "Construtor" : m === "visual" ? "Texto" : "Código HTML"}
              </button>
            ))}
          </div>
          {mode === "builder" ? (
            <div className="pl-6 space-y-3">
              <div className="flex justify-end gap-2 flex-wrap">
                <SuggestionMenu onPick={applyTemplate} />
                <TemplateMenu currentDoc={doc} onApply={applyTemplate} />
              </div>
              <PageBuilder doc={doc} onChange={setDoc} />
            </div>
          ) : mode === "code" ? (
            <div className="space-y-2">
              <Textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                rows={22}
                spellCheck={false}
                className="font-mono text-xs"
                placeholder="<h2>Título</h2><p>Seu conteúdo...</p>"
              />
              <p className="text-xs text-muted-foreground">
                Dica: grades usam <code>&lt;div data-type="columns" data-cols="1fr 2fr 1fr"&gt;&lt;div data-type="column"&gt;...&lt;/div&gt;&lt;/div&gt;</code> e caixas <code>&lt;div data-type="box"&gt;...&lt;/div&gt;</code>. Estilos e scripts personalizados são removidos por segurança.
              </p>
            </div>
          ) : (
          <div className="border border-border rounded-lg">
            <div className="flex flex-wrap gap-1 border-b border-border p-2">
              {toolbarBtn(() => editor?.chain().focus().toggleBold().run(), <Bold size={16} />, "Negrito", editor?.isActive("bold"))}
              {toolbarBtn(() => editor?.chain().focus().toggleItalic().run(), <Italic size={16} />, "Itálico", editor?.isActive("italic"))}
              {toolbarBtn(() => editor?.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 size={16} />, "Título 2", editor?.isActive("heading", { level: 2 }))}
              {toolbarBtn(() => editor?.chain().focus().toggleHeading({ level: 3 }).run(), <Heading3 size={16} />, "Título 3", editor?.isActive("heading", { level: 3 }))}
              {toolbarBtn(() => editor?.chain().focus().toggleBulletList().run(), <List size={16} />, "Lista", editor?.isActive("bulletList"))}
              {toolbarBtn(() => editor?.chain().focus().toggleOrderedList().run(), <ListOrdered size={16} />, "Lista numerada", editor?.isActive("orderedList"))}
              {toolbarBtn(() => editor?.chain().focus().toggleBlockquote().run(), <Quote size={16} />, "Citação", editor?.isActive("blockquote"))}
              {toolbarBtn(addLink, <LinkIcon size={16} />, "Adicionar link", editor?.isActive("link"))}
              {toolbarBtn(addImage, <ImageIcon size={16} />, "Adicionar imagem")}
              <span className="w-px bg-border mx-1" />
              {toolbarBtn(() => editor?.chain().focus().undo().run(), <Undo size={16} />, "Desfazer")}
              {toolbarBtn(() => editor?.chain().focus().redo().run(), <Redo size={16} />, "Refazer")}
              <span className="w-px bg-border mx-1" />
              <TemplateMenu compact onApply={applyTemplate} />
              <SuggestionMenu compact onPick={applyTemplate} />
              {toolbarBtn(() => setLibTarget("inline"), <Library size={16} />, "Imagem da biblioteca")}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadInlineImage(f);
                e.target.value = "";
              }}
            />
            <EditorContent editor={editor} />
          </div>
          )}
          {(() => {
            const words = countWords(mode === "builder" ? serializeDoc(doc) : getContent());
            return (
              <p className="text-xs text-muted-foreground text-right pt-1">
                {words.toLocaleString("pt-BR")} palavras • ~{Math.max(1, Math.round(words / 200))} min de leitura
              </p>
            );
          })()}
        </div>

        <div className="space-y-4">
          {review && <ReviewPanel review={review} onClose={() => setReview(null)} />}
          <div>
            <label className="text-sm font-medium">Slug (URL)</label>
            <Input
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(generateSlug(e.target.value));
              }}
              placeholder="url-do-artigo"
            />
            <p className="text-xs text-muted-foreground mt-1">/blog/{slug || "url-do-artigo"}</p>
          </div>
          <div>
            <label className="text-sm font-medium">Categoria</label>
            <Select value={category || "__none"} onValueChange={(v) => setCategory(v === "__none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Sem categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Sem categoria</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Resumo</label>
            <Textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Resumo curto exibido no card do blog e no Google"
              rows={4}
              maxLength={300}
            />
          </div>
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">SEO</span>
              <Button size="sm" variant="outline" onClick={generateSEO} disabled={seoLoading} className="gap-1.5 h-8">
                {seoLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Gerar SEO com IA
              </Button>
            </div>
            <div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Meta title</span><span className={metaTitle.length > 60 ? "text-destructive" : ""}>{metaTitle.length}/60</span></div>
              <Input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder={title || "Título para o Google"} />
            </div>
            <div>
              <div className="flex justify-between text-xs text-muted-foreground"><span>Meta description</span><span className={metaDescription.length > 155 ? "text-destructive" : ""}>{metaDescription.length}/155</span></div>
              <Textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} rows={3} placeholder="Descrição exibida no Google" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Palavras-chave (separadas por vírgula)</span>
              <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="marketing pet, suplementos..." />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Imagem de capa</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadCover(file);
                e.target.value = "";
              }}
            />
            <div
              tabIndex={0}
              aria-label="Área da capa: arraste, cole ou clique para enviar"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setCoverDrag(true); }}
              onDragLeave={() => setCoverDrag(false)}
              onDrop={(e) => {
                e.preventDefault(); setCoverDrag(false);
                const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith("image/"));
                if (f) uploadCover(f);
              }}
              onPaste={(e) => {
                const f = Array.from(e.clipboardData.files).find((x) => x.type.startsWith("image/"));
                if (f) { e.preventDefault(); uploadCover(f); }
              }}
              className={`mt-1 rounded-lg border-2 border-dashed cursor-pointer overflow-hidden transition-colors outline-none focus-visible:border-primary ${coverDrag ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
            >
              {coverPreview ? (
                <img src={coverPreview} alt="Capa" className="w-full object-cover aspect-video" />
              ) : (
                <div className="aspect-video flex flex-col items-center justify-center gap-1 text-muted-foreground text-sm p-4 text-center">
                  <ImageIcon size={22} />
                  <span>Arraste a imagem aqui, cole com Ctrl+V ou clique para enviar</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" className="flex-1" onClick={() => fileInputRef.current?.click()}>
                {coverPath ? "Trocar imagem" : "Enviar imagem"}
              </Button>
              <Button variant="outline" onClick={() => setLibTarget("cover")} className="gap-1.5"><Library size={15} /> Biblioteca</Button>
            </div>
          </div>
        </div>
      </div>

      <MediaPicker open={!!libTarget} onOpenChange={(o) => !o && setLibTarget(null)} onPick={(m) => { pickMedia(m.url, m.path); setLibTarget(null); }} />

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Sair sem salvar?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Você tem alterações que ainda não foram salvas neste artigo.</p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setLeaveOpen(false)}>Continuar editando</Button>
            <Button variant="outline" onClick={() => { dirtyRef.current = false; setLeaveOpen(false); onClose(false); }}>Sair sem salvar</Button>
            <Button onClick={() => { setLeaveOpen(false); save("draft"); }} disabled={saving}>Salvar rascunho</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[min(1340px,96vw)] h-[92vh] flex flex-col p-4">
          <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
            <DialogTitle>Prévia do artigo</DialogTitle>
            <div className="flex gap-1 p-1 rounded-lg bg-muted">
              {(["desktop", "mobile"] as const).map((d) => (
                <button key={d} type="button" onClick={() => setDevice(d)} className={`px-3 py-1 text-sm rounded-md flex items-center gap-1.5 ${device === d ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}>
                  {d === "desktop" ? <Monitor size={14} /> : <Smartphone size={14} />}{d === "desktop" ? "Desktop" : "Mobile"}
                </button>
              ))}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted rounded-lg flex justify-center p-3">
            <iframe
              title="Prévia"
              src="/admin/preview"
              className="bg-background rounded-md shadow-lg h-full transition-all"
              style={{ width: device === "desktop" ? 1280 : 375, minWidth: device === "desktop" ? 1280 : 375 }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

type Review = { overall: number; summary: string; criteria: { name: string; score: number; feedback: string; suggestions: string[] }[] };

const scoreColor = (n: number) => (n >= 8 ? "text-green-700 bg-green-100" : n >= 6 ? "text-primary bg-primary/15" : "text-destructive bg-destructive/10");

const ReviewPanel = ({ review, onClose }: { review: Review; onClose: () => void }) => (
  <div className="rounded-xl border border-primary/40 bg-card shadow-sm p-3 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-heading font-semibold flex items-center gap-1.5"><Wand2 size={15} className="text-primary" /> Copiloto editorial</span>
      <div className="flex items-center gap-1">
        <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${scoreColor(review.overall)}`}>{review.overall.toFixed(1)}</span>
        <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={14} /></button>
      </div>
    </div>
    <p className="text-sm text-muted-foreground">{review.summary}</p>
    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
      {review.criteria.map((c) => (
        <div key={c.name} className="border-t border-border pt-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{c.name}</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${scoreColor(c.score)}`}>{c.score}/10</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{c.feedback}</p>
          <ul className="mt-1 space-y-1">
            {c.suggestions.map((s, i) => <li key={i} className="text-xs pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-primary">{s}</li>)}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const SuggestionMenu = ({ onPick, compact }: { onPick: (d: Doc) => void; compact?: boolean }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={compact ? "p-2 rounded hover:bg-muted flex items-center gap-1 text-sm" : "h-9 px-3 rounded-md border border-input bg-background text-sm flex items-center gap-1.5 hover:border-primary"}
        title="Modelos por objetivo"
      >
        <Sparkles size={15} className="text-primary" /> {compact ? "" : "Modelos por objetivo"}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 w-80 rounded-xl border border-border bg-background shadow-lg p-2 space-y-1">
            {LAYOUT_SUGGESTIONS.map((l) => (
              <button key={l.key} type="button" onClick={() => { onPick(l.build()); setOpen(false); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary/10">
                <p className="text-sm font-medium">{l.name}</p>
                <p className="text-xs text-muted-foreground">{l.desc}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BlogManager;
